import * as yup from 'yup';
import phone from 'phone';

export const transformSelectValue = (value: any) => {
  if (value && typeof value === 'object' && value.value !== undefined) {
    return value.value;
  }
  return value;
};

export const createSelectField = (errorMessage: string) => {
  return yup
    .mixed()
    .transform(transformSelectValue)
    .test('is-string', errorMessage, (value) => typeof value === 'string')
    .required(errorMessage);
};

export const createRequiredTextField = (fieldName: string) => {
  return yup.string().trim().required(`${fieldName} is required`);
};

export const createOptionalTextField = () => {
  return yup.string().trim();
};

export const createEmailField = (label: string = 'Email') => {
  return yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .required(`${label} is required`);
};

export const createUrlField = (fieldName: string) => {
  return yup
    .string()
    .trim()
    .test('is-url', 'Please enter a valid URL (e.g., example.com or https://example.com)', (value) => {
      if (!value) return false;
      return isValidUrlValue(value);
    })
    .required(`${fieldName} is required`);
};

export const isValidUrlValue = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  const urlToTest = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(urlToTest);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
};

export const validateRequiredUrl = (
  value: string,
  fieldName: string
): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return `${fieldName} is required`;
  }
  if (!isValidUrlValue(trimmed)) {
    return 'Please enter a valid URL (e.g., example.com or https://example.com)';
  }
  return null;
};

export const extractDomain = (value: string): string => {
  if (!value) return value;
  return value
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
};

export const createDomainField = (fieldName: string) => {
  return yup
    .string()
    .trim()
    .test('is-domain', `Please enter a valid domain (e.g., example.com or https://example.com)`, (value) => {
      if (!value) return false;

      const cleanValue = extractDomain(value);

      const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

      return domainPattern.test(cleanValue);
    })
    .required(`${fieldName} is required`);
};

export const createEinField = () => {
  return yup
    .string()
    .trim()
    .matches(/^\d{2}-\d{7}$/, 'Please enter a valid EIN format (XX-XXXXXXX)')
    .required('EIN is required');
};

export const createPhoneField = (label: string = 'Phone number') => {
  return yup
    .string()
    .trim()
    .test('is-phone', 'Please enter a valid phone number', (value) => {
      if (!value) return false;
      const phoneResult = phone(value);
      return phoneResult.isValid;
    })
    .required(`${label} is required`);
};

export const createPostalCodeField = () => {
  return yup
    .string()
    .trim()
    .matches(/^\d{5}(-\d{4})?$/, 'Please enter a valid US ZIP code (12345 or 12345-6789)')
    .required('ZIP code is required');
};
